import { useEffect, useState } from "react";
import {
  X,
  CheckCircle,
  Star,
  ArrowRight,
  Lock,
  Spinner,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useUpgradeModal } from "../context/UpgradeModalContext";
import { useAuth } from "../context/AuthContext";

const TIER_ORDER = ["basic", "team", "pro"];
const TIER_BLURB = {
  basic: "Solo operator · 2 users",
  team: "Growing shop · 5 users · staff role + digests",
  pro: "Full operation · 10 users · financial reports",
};

export default function UpgradeModal() {
  const { open, payload, close } = useUpgradeModal();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get("/billing/config")
      .then((r) => {
        if (cancelled) return;
        const list = (r.data?.plans || [])
          .slice()
          .sort(
            (a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier)
          );
        setPlans(list);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const currentTier = payload?.currentTier || user?.plan?.tier || "basic";
  const reason = payload?.reason || "feature_not_in_plan";
  const message =
    payload?.message ||
    (reason === "user_limit_reached"
      ? "You've reached the user limit on your current plan."
      : "This feature is not included in your current plan.");

  const onManageBilling = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin,
      });
      // Hard redirect to Stripe-hosted portal where the customer can change plan
      window.location.href = data.url;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setRedirecting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0F172A]/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={close}
      data-testid="upgrade-modal-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white border-2 border-[#1A2332] w-full max-w-3xl my-8 sm:my-0"
        data-testid="upgrade-modal"
      >
        {/* Header */}
        <div className="bg-[#1A2332] text-white px-6 sm:px-8 py-5 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 bg-[#FF9800] text-[#1A2332] flex items-center justify-center flex-shrink-0">
              <Lock size={18} weight="duotone" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF9800]">
                Upgrade required
              </div>
              <h2 className="font-display font-bold text-xl sm:text-2xl mt-1 leading-tight">
                {message}
              </h2>
            </div>
          </div>
          <button
            onClick={close}
            data-testid="upgrade-modal-close"
            className="p-1.5 hover:bg-white/10 flex-shrink-0"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Plans */}
        <div className="px-6 sm:px-8 py-6">
          <p className="text-sm text-zinc-600 mb-5">
            Pick a plan that fits your shop. Plan changes through Stripe's
            secure portal — proration is automatic, no re-checkout needed.
          </p>

          {plans.length === 0 ? (
            <div
              className="py-10 text-center text-zinc-500 font-mono text-xs uppercase tracking-widest"
              data-testid="upgrade-modal-loading"
            >
              Loading plans…
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              {plans.map((p) => {
                const isCurrent = p.tier === currentTier;
                const isFeatured = p.tier === "team" && currentTier !== "team";
                const dollars = Math.floor((p.amount_cents || 0) / 100);
                return (
                  <div
                    key={p.tier}
                    data-testid={`upgrade-modal-plan-${p.tier}`}
                    className={`relative border-2 p-4 flex flex-col ${
                      isCurrent
                        ? "border-zinc-300 bg-zinc-50 opacity-75"
                        : isFeatured
                        ? "border-[#FF9800] bg-[#FFF8EE]"
                        : "border-[#1A2332] bg-white"
                    }`}
                  >
                    {isCurrent && (
                      <div className="absolute -top-2.5 left-3 bg-zinc-700 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5">
                        Current
                      </div>
                    )}
                    {isFeatured && !isCurrent && (
                      <div className="absolute -top-2.5 left-3 bg-[#FF9800] text-[#1A2332] text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 flex items-center gap-1">
                        <Star size={9} weight="fill" /> Popular
                      </div>
                    )}

                    <div className="font-display font-bold text-base text-[#0F172A]">
                      {p.label?.replace("ezItemizer ", "") || p.tier}
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-600 leading-snug">
                      {TIER_BLURB[p.tier]}
                    </div>
                    <div className="mt-3 flex items-end gap-1">
                      <span className="font-display font-black text-3xl text-[#0F172A] leading-none">
                        ${dollars}
                      </span>
                      <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest pb-1">
                        /mo
                      </span>
                    </div>

                    <ul className="mt-3 space-y-1.5 flex-1">
                      <li className="flex items-start gap-1.5 text-[11px] text-zinc-700">
                        <CheckCircle
                          size={12}
                          weight="fill"
                          className="text-[#FF9800] flex-shrink-0 mt-0.5"
                        />
                        <span>
                          {p.user_limit} users ·{" "}
                          {p.allowed_roles
                            ?.map((r) => r.replace("_", " "))
                            .join(", ")}
                        </span>
                      </li>
                      {p.features?.financial_reports && (
                        <li className="flex items-start gap-1.5 text-[11px] text-zinc-700">
                          <CheckCircle
                            size={12}
                            weight="fill"
                            className="text-[#FF9800] flex-shrink-0 mt-0.5"
                          />
                          <span>Financial reports + exports</span>
                        </li>
                      )}
                      {p.features?.digest_emails && (
                        <li className="flex items-start gap-1.5 text-[11px] text-zinc-700">
                          <CheckCircle
                            size={12}
                            weight="fill"
                            className="text-[#FF9800] flex-shrink-0 mt-0.5"
                          />
                          <span>End-of-day digest emails</span>
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTAs */}
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-5 border-t border-zinc-200">
            <button
              onClick={close}
              data-testid="upgrade-modal-cancel"
              className="px-5 py-3 text-xs font-bold uppercase tracking-widest border border-zinc-300 hover:bg-zinc-100 text-zinc-700"
            >
              Maybe later
            </button>
            <button
              onClick={onManageBilling}
              disabled={redirecting}
              data-testid="upgrade-modal-manage-billing"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF9800] hover:bg-[#E68900] disabled:opacity-60 disabled:cursor-not-allowed text-[#1A2332] font-bold text-xs uppercase tracking-widest px-5 py-3 transition-colors"
            >
              {redirecting ? (
                <>
                  <Spinner size={14} weight="bold" className="animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  Change plan via Stripe
                  <ArrowRight size={14} weight="bold" />
                </>
              )}
            </button>
          </div>
          <p className="mt-3 text-[10px] text-zinc-500 text-center font-mono uppercase tracking-widest">
            Secure billing portal · powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
}
